import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ConversationUnreadResponse } from "@edu/shared";
import { getConversationUnread } from "../announcements/conversations-api.ts";

/**
 * Свежесть переписки в открытой вкладке: число у раздела «Сообщения» и повод
 * перечитать открытое окно.
 *
 * **Зачем.** Просьба владельца 29.08.2026 дословно: «хочу более интерактивно:
 * проверяй каждые 5 сек или типа того не появилось ли новых сообщений. так же
 * хочу чтобы была подсказка сколько новых сообщений есть у пользователя».
 *
 * ⚠️ **Это НЕ транспорт уведомлений, и раздела 4 CLAUDE.md он не касается.**
 * Наружу не уходит ничего: число показывается тому, кто уже открыл кабинет и
 * держит вкладку открытой. Закрыл вкладку, и продукт молчит; ни одна строка
 * экрана обратного не обещает. Запрет там на доставку критичного через чужие
 * мессенджеры, а не на подпись в собственном интерфейсе.
 *
 * **Четыре правила опроса, и каждое закрывает свою поломку.**
 *
 * 1. **Пять секунд между запросами**, и отсчитываются они от ОТВЕТА, а не от
 *    начала предыдущего запроса: `setInterval` на медленном канале выстраивает
 *    очередь запросов, которая уже не рассосётся
 * 2. ⚠️ **Скрытая вкладка не спрашивает вовсе.** Не «спрашивает реже»: у
 *    человека с десятком фоновых вкладок кабинета это десяток запросов каждые
 *    пять секунд ни для кого. Возвращает опрос `visibilitychange`, и первый
 *    запрос после возвращения уходит немедленно, потому что именно в этот момент
 *    число и смотрят
 * 3. **Отступ при обрыве**: 10, 20, 40, 80 секунд и дальше по две минуты. Сеть в
 *    наших условиях рвётся штатно (раздел 3 CLAUDE.md), и вкладка, долбящая
 *    лежащий API дважды в секунду, мешает ему встать
 * 4. **Ни одного запроса внахлёст.** Флаг `running` держится вокруг всего
 *    похода, а следующий планируется в `finally`: без этого медленный ответ
 *    накладывался бы на свежий, и число прыгало бы назад
 *
 * ⚠️ **Опрос называет серверу открытое окно** (`watch`), и это не удобство.
 * Строк счётчик возит не больше полусотни, самых свежих; разговор за этой чертой
 * не получал строки никогда, а `freshAt` спрашивает именно про открытое окно.
 * То есть у человека с полусотней разговоров окно на пятьдесят первом не
 * узнавало о новом сообщении НИКОГДА, при том что число у раздела его считало:
 * поломка, которая не показывает себя ничем.
 *
 * ⚠️ **Перерисовка идёт только по ИЗМЕНЕНИЮ, а не по каждому ответу.** Ответ это
 * новый объект, и класть его в состояние двенадцать раз в минуту значило бы
 * перерисовывать весь кабинет поверх любой работы, включая набранный и не
 * отправленный текст в соседней форме. Поэтому у ответа считается подпись, и
 * состояние меняется, только когда подпись другая.
 */

/** Как часто спрашиваем. Просьба владельца дословно: «каждые 5 сек или типа того». */
const POLL_MS = 5000;
const BACKOFF_START_MS = 10_000;
const BACKOFF_MAX_MS = 120_000;

export interface ConversationNews {
  /** Сколько новых сообщений у человека ВСЕГО. Ноль означает «метки нет вовсе». */
  total: number;
  /**
   * Время последнего сообщения разговора, `null` у неизвестного ключа.
   *
   * Ради этого поля счётчик и возит время: открытое окно узнаёт, что пора
   * перечитать себя, не спрашивая свою страницу заново каждые пять секунд.
   */
  freshAt: (conversationId: string) => number | null;
  /**
   * Подпись всего ответа. Меняется ровно тогда, когда изменилась хоть одна
   * строка, и служит поводом перечитать СПИСОК разговоров.
   */
  stamp: string;
  /** Спросить немедленно: после отправки сообщения и после открытия окна. */
  refresh: () => void;
  /**
   * Какое окно человек читает прямо сейчас. `null` означает «никакого».
   *
   * ⚠️ **Без этого счётчик слеп на пятьдесят первом разговоре, и слеп молча.**
   * Строк он возит не больше полусотни, самых свежих, а `freshAt` спрашивает
   * ровно про открытое окно: разговор за этой чертой не получал строки никогда,
   * то есть новое сообщение в нём не доезжало до читателя, хотя число у раздела
   * его считало. Открытое окно называется серверу, и он поднимает его строку в
   * начало.
   */
  watch: (conversationId: string | null) => void;
}

const EMPTY: ConversationNews = {
  total: 0,
  freshAt: () => null,
  stamp: "",
  refresh: () => {},
  watch: () => {},
};

/**
 * Подпись ответа: всё, из-за чего экранам стоит шевелиться.
 *
 * Число непрочитанного здесь тоже есть, и это не лишнее: список разговоров
 * показывает его столбиком, значит погашенный в соседней вкладке счётчик обязан
 * погаснуть и здесь.
 */
function signatureOf(res: ConversationUnreadResponse): string {
  return `${String(res.total)}|${res.items
    .map((i) => `${i.id}:${String(i.unread)}:${String(i.at)}`)
    .join(",")}`;
}

function backoffMs(fails: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_START_MS * 2 ** (fails - 1));
}

/**
 * Опрос. `enabled` это «есть сессия»: спрашивать разговоры у того, кто ещё не
 * вошёл, нечем и незачем.
 *
 * ⚠️ Условие уезжает параметром, а не ветвлением вокруг вызова: состав хуков
 * обязан совпадать от рендера к рендеру, а сессия приезжает позже первого
 * рендера.
 */
export function useConversationNews(enabled: boolean): ConversationNews {
  const [news, setNews] = useState<ConversationUnreadResponse | null>(null);
  const [stamp, setStamp] = useState("");
  /** Живая ручка «спросить сейчас». Переставляется эффектом на каждый его запуск. */
  const now = useRef<() => void>(() => {});
  /**
   * Открытое окно. Через ref, а не через состояние: опрос читает его в момент
   * запроса, и перезапускать из-за смены окна ни таймер, ни эффект не надо.
   */
  const opened = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setNews(null);
      setStamp("");
      return;
    }

    let alive = true;
    let timer: number | null = null;
    let running = false;
    let fails = 0;
    let seen = "";

    const clear = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const plan = (ms: number) => {
      clear();
      if (!alive) return;
      timer = window.setTimeout(() => void ask(), ms);
    };

    const ask = async () => {
      timer = null;
      if (!alive || running) return;
      /*
        Скрытая вкладка не спрашивает и НЕ планирует следующий заход: разбудит её
        `visibilitychange`. Планировать «на всякий случай» значило бы держать
        таймер в каждой фоновой вкладке до конца сессии.
      */
      if (document.visibilityState === "hidden") return;

      running = true;
      try {
        const res = await getConversationUnread(opened.current ?? undefined);
        if (!alive) return;
        fails = 0;
        const next = signatureOf(res);
        // Перерисовка только по изменению: разбор в шапке файла
        if (next !== seen) {
          seen = next;
          setNews(res);
          setStamp(next);
        }
      } catch {
        /*
          Отказ проглатывается молча и число просто не меняется. Это не лень:
          подсказка о новых сообщениях это справка, а не работа человека, и
          красная строка «не удалось прочитать счётчик» поверх любого экрана
          кабинета была бы хуже устаревшего числа. То же правило у полоски квоты
          и у числа домашки.
        */
        fails += 1;
      } finally {
        running = false;
        if (alive) plan(fails === 0 ? POLL_MS : backoffMs(fails));
      }
    };

    const wake = () => {
      if (document.visibilityState === "visible") {
        // Вернувшемуся человеку число нужно сейчас, а не через пять секунд, и
        // отступ обнуляется вместе с этим: сеть могла починиться, пока он ушёл
        fails = 0;
        plan(0);
      } else {
        clear();
      }
    };

    now.current = () => plan(0);
    document.addEventListener("visibilitychange", wake);
    void ask();

    return () => {
      alive = false;
      clear();
      document.removeEventListener("visibilitychange", wake);
      now.current = () => {};
    };
  }, [enabled]);

  const refresh = useCallback(() => now.current(), []);
  const watch = useCallback((id: string | null) => {
    opened.current = id;
  }, []);

  return useMemo(() => {
    if (news === null) return { ...EMPTY, refresh, watch };
    const at = new Map(news.items.map((i) => [i.id, i.at]));
    return {
      total: news.total,
      freshAt: (id: string) => at.get(id) ?? null,
      stamp,
      refresh,
      watch,
    };
  }, [news, stamp, refresh, watch]);
}

/**
 * Тот же счёт, доставленный экранам кабинета.
 *
 * ⚠️ **Опрос ОДИН на всю вкладку, и живёт он в каркасе.** Список разговоров и
 * открытое окно оба хотят знать «не появилось ли нового», и свой таймер у
 * каждого означал бы два запроса вместо одного ровно там, где человек и так
 * держит оба экрана по очереди.
 *
 * Умолчание пустое: контекста нет у экранов вне каркаса кабинета (комната урока,
 * архив, погашение ссылки). Пустой счёт, а не отказ, потому что для тех экранов
 * это не ошибка: числа там нет вовсе.
 */
const NewsContext = createContext<ConversationNews>(EMPTY);

export const ConversationNewsProvider = NewsContext.Provider;

export function useConversationNewsValue(): ConversationNews {
  return useContext(NewsContext);
}
